import { useMutation, useReactiveVar } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { Button, FieldInput, Meta, theme } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import config from 'config/config';
import { CHALLENGE_CUSTOMER } from 'graphql/mutations/challengeCustomer';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { country } from 'states/persistInStorage';
import { useBenefitStore } from 'stores/benefitStore';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  ExternalVerificationType,
  Mutation,
  MutationChallengeCustomerArgs,
} from 'types/generatedSchemaTypes';
import { RootNavProps } from 'types/navigation';
import { isWeb } from 'utils/deviceUtils';
import validator from 'validator';
import { useUiStore } from 'stores/uiStore';

enum ExternalVerification {
  EMAIL = 'EMAIL',
  HSA = 'HSA',
}

const EmailVerificationModal: React.FC = () => {
  const { t } = useTranslation();
  const { navigate } = useNavigation<RootNavProps>();
  const countryCode = useReactiveVar(country);
  const { prefillEmail, setPrefillEmail } = useBenefitStore();
  const [email, setEmail] = useState(prefillEmail);
  const [errorMessage, setErrorMessage] = useState('');

  const [challengeCustomer, { loading }] = useMutation<
    Mutation,
    MutationChallengeCustomerArgs
  >(CHALLENGE_CUSTOMER, {
    onCompleted: (data) => {
      const result = data.challengeCustomer;
      if (result?.status === 'PENDING') {
        useUiStore.getState().setSheetModal('');
        navigate('benefitVerification', {
          verificationId: result.id,
          externalVerificationType: ExternalVerification.EMAIL,
          email,
        });
      }
      if (result?.status === 'FAILED') {
        setErrorMessage(t('account.emailVerificationModal.verificationFailed'));
      }
    },
    onError: (error) => {
      if (
        error.graphQLErrors.some(
          (e) => e.extensions?.code === 'VERIFICATION_EMAIL_ALREADY_ASSOCIATED'
        )
      ) {
        setErrorMessage(
          t(
            'account.emailVerificationModal.verificationEmailAlreadyAssociated',
            { email }
          )
        );
      }
    },
  });

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (errorMessage) setErrorMessage('');
  };

  const handleSend = async (
    externalVerificationType: ExternalVerificationType
  ) => {
    if (!validator.isEmail(email)) {
      setErrorMessage(t('account.emailVerificationModal.invalidEmail'));
      return;
    }
    await challengeCustomer({
      variables: {
        input: {
          externalVerificationType: externalVerificationType,
          verificationSubjectId: email,
          verificationMethod: 'USER_INTERACTIVE',
          countryCode,
          redirectUrl: isWeb
            ? `${config.redirectUriWeb}/account/benefits`
            : 'com.aimoone://account',
        },
      },
    });
  };

  const handleCancel = () => {
    setPrefillEmail('');
    useUiStore.getState().setSheetModal('');
  };

  return (
    <View>
      <StyledWrapper mb={20}>
        <Meta color={theme.colors.black}>
          {t('account.emailVerificationModal.description')}
        </Meta>
      </StyledWrapper>
      <StyledWrapper mb={30}>
        <FieldInput
          testID="benefit-email-input"
          title={t('account.emailVerificationModal.emailLabel')}
          value={email}
          onChangeText={handleEmailChange}
          placeholder={t('account.emailVerificationModal.emailPlaceholder')}
          isSheet
          errorMessage={errorMessage ?? undefined}
          errorIcon={
            <View
              style={{
                width: 16,
                height: 16,
                alignSelf: 'center',
              }}>
              <Icon
                name="Info-Filled"
                color={theme.colors.aimoUiRed}
                size={16}
              />
            </View>
          }
        />
      </StyledWrapper>
      <StyledWrapper mb={10}>
        <Button
          testID="send-verification-email-button"
          text={t('account.emailVerificationModal.sendVerificationEmail')}
          size="l"
          onPress={() => handleSend(ExternalVerification.EMAIL)}
          disabled={!email || loading || errorMessage !== ''}
          loading={loading}
        />
      </StyledWrapper>
      <StyledWrapper mb={0}>
        <Button
          testID="cancel-connect-benefit-button"
          text={t('account.emailVerificationModal.cancel')}
          size="l"
          buttonType="secondary"
          onPress={handleCancel}
        />
      </StyledWrapper>
    </View>
  );
};

export default EmailVerificationModal;
